import { QueryClientProvider } from "@tanstack/react-query";
import { CloudOff, Loader2, Users } from "lucide-react";
import { useLayoutEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import { adminApi } from "@/api/adminApi";
import { isApiError } from "@/api/errors";
import { setSharedHandler } from "@/api/http";
import { useSharedRunInfo } from "@/api/queries";
import { useSession } from "@/features/auth/session";
import { createSharedHandler } from "@/api/sharedSource";
import { createQueryClient } from "@/app/queryClient";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { formatRelative } from "@/lib/format";
import { SharedContext } from "./SharedContext";

function Inner({ runId, children }: { runId: string; children: ReactNode }) {
  const session = useSession();
  const [client] = useState(() => createQueryClient());
  const [ready, setReady] = useState(false);
  const signedIn = session.status === "active";
  const info = useSharedRunInfo(runId, signedIn);

  useLayoutEffect(() => {
    setSharedHandler(createSharedHandler(runId, (name) => adminApi.sharedObject(runId, name)));
    setReady(true);
    return () => {
      setSharedHandler(null);
      setReady(false);
    };
  }, [runId]);

  // The page's own session gate shows the wait, or sends the visitor to sign in and back here.
  if (!signedIn) return <>{children}</>;
  if (info.isLoading) {
    return (
      <div role="status" className="mt-24 flex justify-center text-muted-foreground">
        <Loader2 aria-hidden className="animate-spin" />
        <span className="sr-only">Loading the shared run</span>
      </div>
    );
  }
  if (info.error) {
    if (isApiError(info.error) && info.error.code === "NOT_FOUND") {
      return (
        <EmptyState
          icon={CloudOff}
          title="This shared run isn't here"
          description="The link may be wrong, or the tester's ATS hasn't uploaded the run yet: ask them to open ATS and try again in a minute. If this is your own run, start ATS on your computer to open it."
        />
      );
    }
    return <ErrorState error={info.error} onRetry={() => void info.refetch()} title="Couldn't open the shared run" />;
  }

  const run = info.data;
  return (
    <SharedContext.Provider value={{ runId, info: run }}>
      {run && (
        <div
          role="status"
          className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border bg-muted/40 px-3 py-2 text-sm no-print"
        >
          <Users aria-hidden className="size-4 text-muted-foreground" />
          <span>
            Shared by <strong>{run.owner.name || run.owner.email}</strong>
            {run.hostname ? ` from ${run.hostname}` : ""}. Read-only.
          </span>
          <span className="text-muted-foreground">
            {run.complete
              ? `Complete · updated ${formatRelative(run.updated_at)}`
              : "Still syncing from the tester's computer. This page updates by itself."}
          </span>
        </div>
      )}
      <QueryClientProvider client={client}>{ready ? children : null}</QueryClientProvider>
    </SharedContext.Provider>
  );
}

export function SharedRunScope({ children }: { children: ReactNode }) {
  const { runId = "" } = useParams();
  return (
    <Inner key={runId} runId={runId}>
      {children}
    </Inner>
  );
}
